import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Activity, Loader2 } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { adminListEvents } from "@/lib/admin-security.functions";

const EVENT_TYPES = ["", "login", "device_registered", "device_blocked", "banned", "unbanned", "force_logout", "session_reset"] as const;

export const Route = createFileRoute("/_authenticated/admin/events")({
  head: () => ({ meta: [{ title: "Events — Admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <EventsPage />
    </AdminOnly>
  ),
});

function EventsPage() {
  const [type, setType] = useState<string>("");
  const run = useServerFn(adminListEvents);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-events", type],
    queryFn: () => run({ data: { eventType: type, limit: 300 } }),
    refetchInterval: 15_000,
  });

  const rows = (data?.events ?? []) as Array<{
    id: string; user_id: string | null; device_id: string | null;
    event_type: string; ip_address: string | null; user_agent: string | null;
    meta: unknown; created_at: string;
    profiles: { email: string; full_name: string | null } | null;
  }>;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Activity}
        title="Security events"
        subtitle="Real-time device and auth activity across the platform."
        tone="text-emerald-500"
        glow="hsl(158 84% 45% / 0.30)"
      />


      <div className="flex flex-wrap gap-1">
        {EVENT_TYPES.map((t) => (
          <button
            key={t || "all"}
            type="button"
            onClick={() => setType(t)}
            className={
              "rounded-full px-3 py-1 text-[11px] font-medium transition " +
              (type === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground")
            }
          >
            {t || "All"}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading events…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No events.</div>
        ) : (
          <ul className="divide-y text-sm">
            {rows.map((e) => (
              <li key={e.id} className="flex items-start justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/30">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-mono uppercase ${eventTone(e.event_type)}`}>{e.event_type}</span>
                    <span className="truncate text-xs">
                      {e.profiles?.full_name ?? e.profiles?.email ?? (e.user_id ? "user " + e.user_id.slice(0, 8) : "unknown")}
                    </span>
                  </div>
                  <div className="mt-1 truncate text-[11px] text-muted-foreground">
                    {e.ip_address ? `${e.ip_address} · ` : ""}{(e.user_agent ?? "").slice(0, 120)}
                  </div>
                </div>
                <span className="shrink-0 text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function eventTone(t: string) {
  if (t.includes("block") || t === "banned") return "bg-red-500/10 text-red-500";
  if (t === "login" || t === "device_registered") return "bg-emerald-500/10 text-emerald-500";
  if (t === "force_logout" || t === "session_reset") return "bg-amber-500/10 text-amber-500";
  return "bg-muted text-muted-foreground";
}
